import { useState } from 'react';

import { CoreTypography } from '@youscience/khaleesi';
import { BrightpathHeader } from '@youscience/brightpath-header';
import { UserDocumentWithAccess } from '@youscience/user-service-common';

import { ProfileWithExtendedData, useAuthStore } from '@store/authStore';
import { useHomePageStore } from '@store/homePageStore';
import { useSchoolSelectionStore } from '@store/schoolSelectionStore';
import { useUserPreferencesStore } from '@store/userPreferencesStore';
import { useUserStore } from '@store/userStore';

import { MIN_OPT_IN_USER_AGE, MIN_OPT_IN_USER_GRADE } from '@constants/optIn';

import { AllSchoolConnectionModalProps, withAllSchoolConnectionModal } from '@HOCs/withAllSchoolConnectionModal';

import { isAlreadyGraduated } from '@utils/studentProgress';

import { VerificationMessage } from '@components/Layout/components/Header/components/LPUserMenu/components/VerificationMessage';

import DataSharingIcon from '@images/data-sharing.svg?react';
import { LPUserMenu } from './components/LPUserMenu';

import { StyledManageSharing } from './Header.styles';

import { handleTenantSelectCallback } from './constants';

type HeaderProps = AllSchoolConnectionModalProps;

const Header = (props: HeaderProps) => {
  const { onOpenAllSchoolConnection } = props;

  const authSession = useAuthStore((state) => state.authSession);
  const schoolFavorites = useUserPreferencesStore((state) => state.schoolFavorites);

  const landingPageSchoolData = useHomePageStore((state) => state.landingPageSchoolData);
  const { isCollegeOriginatingTenant } = useSchoolSelectionStore((state) => state.schoolSelectionData);

  const { userData } = useAuthStore((state) => state.authSession);
  const { gradeLevel } = (userData?.profile ?? {}) as ProfileWithExtendedData;

  const alreadyGraduated = isAlreadyGraduated();
  const age = useUserStore.getState().getUserAge();

  const showManageSharing =
    age >= MIN_OPT_IN_USER_AGE && ((gradeLevel || 0) >= MIN_OPT_IN_USER_GRADE || alreadyGraduated);

  const enrolledSchoolId = landingPageSchoolData?.id;

  const [isOpenVerificationMessage, setIsOpenVerificationMessage] = useState(false);

  const handleOpenVerificationMessage = () => {
    setIsOpenVerificationMessage(true);
  };

  const handleCloseVerificationMessage = () => {
    setIsOpenVerificationMessage(false);
  };

  const mode = () => {
    const modes: { [key: string]: 'sandbox' | 'dev' | 'stage' | 'prod' } = {
      sandbox: 'sandbox',
      development: 'dev',
      dev: 'dev',
      stage: 'stage',
      production: 'prod',
      prod: 'prod',
    };

    return modes[process.env.NODE_ENV as keyof typeof modes];
  };

  const userMenu = () => {
    if (enrolledSchoolId && !isCollegeOriginatingTenant) {
      return <LPUserMenu onHandleShowMessage={handleOpenVerificationMessage} />;
    }

    return schoolFavorites.length > 0 && showManageSharing ? (
      <StyledManageSharing onClick={onOpenAllSchoolConnection}>
        <DataSharingIcon />

        <CoreTypography>Manage sharing</CoreTypography>
      </StyledManageSharing>
    ) : null;
  };

  return authSession.userData ? (
    <>
      <BrightpathHeader
        currentProductPage="colleges-programs"
        accessDocumentId={authSession?.currentAccess?.accessDocumentId}
        environment={mode()}
        productSpecificNode={userMenu()}
        handleTenantSelectCallback={handleTenantSelectCallback}
        impersonating={authSession?.isImpersonated || false}
        userDocument={authSession.userData}
      />
      {isOpenVerificationMessage && (
        <VerificationMessage
          isOpenVerificationMessage={isOpenVerificationMessage}
          onCloseVerificationMessage={handleCloseVerificationMessage}
        />
      )}
    </>
  ) : null;
};

export const HeaderWithModal = withAllSchoolConnectionModal(Header);
